import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = (token) => ({ headers: { Authorization: `Bearer ${token}` } });

// Thin client for every Invitations endpoint. Keeps components tidy.
export const invitesApi = {
  pricing: () => axios.get(`${API}/invites/pricing`).then(r => r.data),

  templates: (params) => axios.get(`${API}/invites/templates`, { params }).then(r => r.data),
  template: (id) => axios.get(`${API}/invites/templates/${id}`).then(r => r.data),

  createEvent: (body, token) => axios.post(`${API}/invite-events`, body, auth(token)).then(r => r.data),
  getEvent: (id, token) => axios.get(`${API}/invite-events/${id}`, auth(token)).then(r => r.data),
  fromSession: (sid, token) => axios.get(`${API}/invites/from-session/${sid}`, auth(token)).then(r => r.data),

  createInvitation: (eid, body, token) => axios.post(`${API}/invite-events/${eid}/invitations`, body, auth(token)).then(r => r.data),
  generateInvitation: (eid, body, token) => axios.post(`${API}/invite-events/${eid}/invitations/generate`, body, auth(token)).then(r => r.data),
  generateOptions: (eid, body, token) => axios.post(`${API}/invite-events/${eid}/generate-options`, body, auth(token)).then(r => r.data),
  createFromSpec: (eid, body, token) => axios.post(`${API}/invite-events/${eid}/invitations/from-spec`, body, auth(token)).then(r => r.data),
  remix: (id, body, token) => axios.post(`${API}/invitations/${id}/remix`, body, auth(token)).then(r => r.data),
  assist: (body, token) => axios.post(`${API}/invites/assist`, body, auth(token)).then(r => r.data),
  coverStyles: () => axios.get(`${API}/invites/cover/styles`).then(r => r.data),
  generateCover: (id, body, token) => axios.post(`${API}/invitations/${id}/cover`, body, auth(token)).then(r => r.data),
  getInvitation: (id, token) => axios.get(`${API}/invitations/${id}`, auth(token)).then(r => r.data),
  updateInvitation: (id, body, token) => axios.patch(`${API}/invitations/${id}`, body, auth(token)).then(r => r.data),
  duplicate: (id, token) => axios.post(`${API}/invitations/${id}/duplicate`, {}, auth(token)).then(r => r.data),

  guests: (id, token) => axios.get(`${API}/invitations/${id}/guests`, auth(token)).then(r => r.data),
  addGuests: (id, guests, token) => axios.post(`${API}/invitations/${id}/guests`, { guests }, auth(token)).then(r => r.data),
  importGuests: (id, csv, token) => axios.post(`${API}/invitations/${id}/guests/import`, { csv }, auth(token)).then(r => r.data),
  deleteGuest: (id, gid, token) => axios.delete(`${API}/invitations/${id}/guests/${gid}`, auth(token)).then(r => r.data),
  // AI writes a warm one-line note per named guest (optional guest_ids + vibe hint).
  personalize: (id, body, token) => axios.post(`${API}/invitations/${id}/personalize`, body || {}, auth(token)).then(r => r.data),
  // Host edits/clears a single guest's note by hand.
  setGuestNote: (id, gid, note, token) => axios.patch(`${API}/invitations/${id}/guests/${gid}/note`, { note: note || '' }, auth(token)).then(r => r.data),

  send: (id, body, token) => axios.post(`${API}/invitations/${id}/send`, body, auth(token)).then(r => r.data),

  // Upload a photo/video file → returns a public URL usable in <img>/<video>.
  upload: async (file, token, onProgress) => {
    const form = new FormData();
    form.append('file', file);
    const r = await axios.post(`${API}/upload`, form, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) => { if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100)); },
    });
    return `${process.env.REACT_APP_BACKEND_URL}/api/files/${r.data.path}`;
  },

  publicInvite: (id, guestId) => axios.get(`${API}/i/${id}`, guestId ? { params: { g: guestId } } : undefined).then(r => r.data),
  rsvp: (id, body) => axios.post(`${API}/i/${id}/rsvp`, body).then(r => r.data),

  // Day-of live page — public feed + guest wall, and host broadcast.
  live: (id) => axios.get(`${API}/i/${id}/live`).then(r => r.data),
  postWall: (id, body) => axios.post(`${API}/i/${id}/wall`, body).then(r => r.data),
  announce: (id, body, token) => axios.post(`${API}/invitations/${id}/announce`, body, auth(token)).then(r => r.data),
  deleteAnnounce: (id, annId, token) => axios.delete(`${API}/invitations/${id}/announce/${annId}`, auth(token)).then(r => r.data),

  rsvpDashboard: (id, token) => axios.get(`${API}/invitations/${id}/rsvps`, auth(token)).then(r => r.data),
  remind: (id, token, note) => axios.post(`${API}/invitations/${id}/rsvps/remind`, { note: note || null }, auth(token)).then(r => r.data),
  checkin: (id, gid, token) => axios.post(`${API}/invitations/${id}/guests/${gid}/checkin`, {}, auth(token)).then(r => r.data),
  exportUrl: (id) => `${API}/invitations/${id}/rsvps/export`,

  billing: (token) => axios.get(`${API}/billing/me`, auth(token)).then(r => r.data),
  cancelSub: (token) => axios.post(`${API}/billing/subscription/cancel`, {}, auth(token)).then(r => r.data),
  checkoutTier: (body, token) => axios.post(`${API}/invites/checkout/tier`, body, auth(token)).then(r => r.data),
  checkoutCredits: (body, token) => axios.post(`${API}/invites/checkout/credits`, body, auth(token)).then(r => r.data),
  checkoutSubscription: (body, token) => axios.post(`${API}/invites/checkout/subscription`, body, auth(token)).then(r => r.data),
  checkoutStatus: (sid, token) => axios.get(`${API}/invites/checkout/status/${sid}`, auth(token)).then(r => r.data),
};

export const money = (amt, currency = 'cad') =>
  new Intl.NumberFormat('en-CA', { style: 'currency', currency: (currency || 'cad').toUpperCase() }).format(amt);
